import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Grid, Typography, ToggleButtonGroup, ToggleButton,
  IconButton, TextField, Autocomplete, Switch, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Divider, Tooltip,
  Skeleton, Container, FormControlLabel, alpha,
} from '@mui/material';
import {
  Add, Edit, Delete, ChevronLeft, ChevronRight, Today,
  AttachMoney, Schedule, Close, Check,
} from '@mui/icons-material';
import {
  format, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval,
  isToday,
} from 'date-fns';
import { useTimeTracking } from '../context_providers/TimeTrackingContext';
import { useProjects } from '../context_providers/ProjectContext';
import { useNotification } from '../context_providers/NotificationContext';
import { TimeEntry, CreateTimeEntryRequest, Project } from '../../types';
import apiService from '../../utils/api.service';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const parseDuration = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // HH:MM format
  const hhmmMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (hhmmMatch) return parseInt(hhmmMatch[1]) * 60 + parseInt(hhmmMatch[2]);
  // XhYm format
  const hmMatch = trimmed.match(/^(\d+)h\s*(\d+)m?$/);
  if (hmMatch) return parseInt(hmMatch[1]) * 60 + parseInt(hmMatch[2]);
  // Decimal hours (with optional trailing "h")
  const decimalMatch = trimmed.match(/^(\d+\.?\d*)h?$/);
  if (decimalMatch) return Math.round(parseFloat(decimalMatch[1]) * 60);
  // Minutes only
  const minMatch = trimmed.match(/^(\d+)m$/);
  if (minMatch) return parseInt(minMatch[1]);
  return null;
};

const totalMinutes = (entries: TimeEntry[]): number =>
  entries.reduce((sum, e) => sum + (e.durationMinutes || 0), 0);

const formatDateForApi = (date: Date): string => format(date, 'yyyy-MM-dd');

const getMondayOfWeek = (date: Date): Date =>
  startOfWeek(date, { weekStartsOn: 1 });

// ---------------------------------------------------------------------------
// Quick Entry Bar
// ---------------------------------------------------------------------------

interface QuickEntryBarProps {
  projects: Project[];
  onSubmit: (data: CreateTimeEntryRequest) => Promise<void>;
}

const QuickEntryBar: React.FC<QuickEntryBarProps> = ({ projects, onSubmit }) => {
  const [project, setProject] = useState<Project | null>(null);
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [date, setDate] = useState(formatDateForApi(new Date()));
  const [isBillable, setIsBillable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { showError } = useNotification();
  const [narratives, setNarratives] = useState<any[]>([]);

  useEffect(() => {
    const loadNarratives = async () => {
      try {
        const response = await apiService.listNarratives();
        const data = response.data || response;
        setNarratives(Array.isArray(data) ? data : (data?.narratives || []));
      } catch {
        // Non-critical - narratives are optional
      }
    };
    loadNarratives();
  }, []);

  const activeProjects = useMemo(
    () => projects.filter((p) => p.status === 'active'),
    [projects],
  );

  const handleSubmit = async () => {
    const minutes = parseDuration(duration);
    if (!minutes || minutes <= 0) {
      showError('Enter a valid duration (e.g. 1:30, 1.5, 90m)');
      return;
    }
    if (!description.trim()) {
      showError('Please enter a description');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        projectId: project?.id,
        clientId: project?.clientId,
        date,
        description: description.trim(),
        durationMinutes: minutes,
        isBillable,
      });
      setDescription('');
      setDuration('');
      setProject(null);
    } catch (err: any) {
      showError(err.message || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        border: '1px solid rgba(148, 163, 184, 0.1)',
      }}
    >
      <Autocomplete
        size="small"
        options={activeProjects}
        value={project}
        onChange={(_, val) => {
          setProject(val);
          if (val) setIsBillable(val.isBillable);
        }}
        getOptionLabel={(o) => o.name}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10, height: 10, borderRadius: '50%',
                bgcolor: option.color || '#7b6df6', flexShrink: 0,
              }}
            />
            <Typography variant="body2">{option.name}</Typography>
            {option.code && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {option.code}
              </Typography>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder="Project" variant="outlined" />
        )}
        sx={{ minWidth: 200, flex: '1 1 200px' }}
      />

      <Autocomplete
        size="small"
        freeSolo
        options={narratives}
        getOptionLabel={(option: any) => typeof option === 'string' ? option : option.title || option.text || ''}
        inputValue={description}
        onInputChange={(_, val) => setDescription(val)}
        onChange={(_, val) => {
          if (val && typeof val !== 'string') {
            setDescription(val.text || val.title || '');
          }
        }}
        renderOption={(props, option: any) => (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.title}</Typography>
              {option.text && option.text !== option.title && (
                <Typography variant="caption" color="text.secondary">{option.text}</Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={narratives.length > 0 ? "Type or select a narrative..." : "What did you work on?"}
            onKeyDown={handleKeyDown}
          />
        )}
        sx={{ flex: '2 1 250px' }}
      />

      <TextField
        size="small"
        placeholder="1:30"
        value={duration}
        onChange={(e) => setDuration(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{ width: 90 }}
        inputProps={{ style: { textAlign: 'center' } }}
      />

      <TextField
        size="small"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        sx={{ width: 150 }}
      />

      <Tooltip title={isBillable ? 'Billable' : 'Non-billable'}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={isBillable}
              onChange={(e) => setIsBillable(e.target.checked)}
              color="secondary"
            />
          }
          label={<AttachMoney sx={{ fontSize: 18, color: isBillable ? '#10b981' : 'text.disabled' }} />}
          sx={{ m: 0 }}
        />
      </Tooltip>

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={handleSubmit}
        disabled={submitting}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Add
      </Button>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Time Entry Row
// ---------------------------------------------------------------------------

interface TimeEntryRowProps {
  entry: TimeEntry;
  project?: Project;
  isEditing: boolean;
  allProjects: Project[];
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (id: string, data: Partial<TimeEntry>) => Promise<void>;
  onDelete: (id: string) => void;
}

const TimeEntryRow: React.FC<TimeEntryRowProps> = ({
  entry, project, isEditing, allProjects, onStartEdit, onCancelEdit, onSave, onDelete,
}) => {
  const [editProject, setEditProject] = useState<Project | null>(
    allProjects.find((p) => p.id === entry.projectId) || null,
  );
  const [editDesc, setEditDesc] = useState(entry.description);
  const [editDuration, setEditDuration] = useState(formatDuration(entry.durationMinutes));
  const [saving, setSaving] = useState(false);
  const { showError } = useNotification();

  const handleSave = async () => {
    const minutes = parseDuration(editDuration);
    if (!minutes || minutes <= 0) {
      showError('Enter a valid duration');
      return;
    }
    setSaving(true);
    try {
      await onSave(entry.id, {
        projectId: editProject?.id,
        clientId: editProject?.clientId,
        description: editDesc.trim(),
        durationMinutes: minutes,
      });
      onCancelEdit();
    } catch (err: any) {
      showError(err.message || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const color = project?.color || '#7b6df6';

  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, flexWrap: 'wrap',
          borderLeft: `3px solid ${color}`, bgcolor: 'rgba(123, 109, 246, 0.04)',
          borderRadius: 1,
        }}
      >
        <Autocomplete
          size="small"
          options={allProjects.filter((p) => p.status === 'active')}
          value={editProject}
          onChange={(_, val) => setEditProject(val)}
          getOptionLabel={(o) => o.name}
          renderInput={(params) => <TextField {...params} placeholder="Project" />}
          sx={{ minWidth: 180, flex: '1 1 180px' }}
        />
        <TextField
          size="small"
          value={editDesc}
          onChange={(e) => setEditDesc(e.target.value)}
          sx={{ flex: '2 1 200px' }}
        />
        <TextField
          size="small"
          value={editDuration}
          onChange={(e) => setEditDuration(e.target.value)}
          sx={{ width: 90 }}
          inputProps={{ style: { textAlign: 'center' } }}
        />
        <IconButton size="small" onClick={handleSave} disabled={saving} color="primary">
          <Check fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onCancelEdit}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
        borderLeft: `3px solid ${color}`, borderRadius: 1,
        transition: 'background 0.15s',
        '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.04)' },
      }}
    >
      <Box sx={{ flex: '0 0 auto', minWidth: 120 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color }}>
          {project?.name || 'No Project'}
        </Typography>
        {entry.taskId && (
          <Typography variant="caption" color="text.secondary">Task</Typography>
        )}
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flex: '1 1 200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {entry.description || '--'}
      </Typography>

      <Chip
        label={formatDuration(entry.durationMinutes)}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600, borderColor: 'rgba(148, 163, 184, 0.15)',
          minWidth: 70, justifyContent: 'center',
        }}
      />

      {entry.isBillable && (
        <Tooltip title="Billable">
          <AttachMoney sx={{ fontSize: 18, color: '#10b981' }} />
        </Tooltip>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onStartEdit}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => onDelete(entry.id)} color="error">
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Day View
// ---------------------------------------------------------------------------

interface DayViewProps {
  entries: TimeEntry[];
  projects: Project[];
  editingEntryId: string | null;
  setEditingEntryId: (id: string | null) => void;
  onSave: (id: string, data: Partial<TimeEntry>) => Promise<void>;
  onDelete: (id: string) => void;
}

const DayView: React.FC<DayViewProps> = ({
  entries, projects, editingEntryId, setEditingEntryId, onSave, onDelete,
}) => {
  const getProject = useCallback(
    (id?: string) => (id ? projects.find((p) => p.id === id) : undefined),
    [projects],
  );

  // Group entries by projectId
  const grouped = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    entries.forEach((e) => {
      const key = e.projectId || '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return Array.from(map.entries());
  }, [entries]);

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Schedule sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No time entries for today
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Use the quick entry bar above or start a timer to begin tracking.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {grouped.map(([projectId, groupEntries]) => {
        const project = getProject(projectId === '__none__' ? undefined : projectId);
        return (
          <Box key={projectId}>
            {grouped.length > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 1.5, mb: 0.5, display: 'block' }}
              >
                {project?.name || 'No Project'} -- {formatDuration(totalMinutes(groupEntries))}
              </Typography>
            )}
            {groupEntries.map((entry) => (
              <TimeEntryRow
                key={entry.id}
                entry={entry}
                project={project}
                allProjects={projects}
                isEditing={editingEntryId === entry.id}
                onStartEdit={() => setEditingEntryId(entry.id)}
                onCancelEdit={() => setEditingEntryId(null)}
                onSave={onSave}
                onDelete={onDelete}
              />
            ))}
          </Box>
        );
      })}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          Total: {formatDuration(totalMinutes(entries))}
        </Typography>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Week View
// ---------------------------------------------------------------------------

interface WeekViewProps {
  weekStart: Date;
  entries: TimeEntry[];
  projects: Project[];
  editingEntryId: string | null;
  setEditingEntryId: (id: string | null) => void;
  onSave: (id: string, data: Partial<TimeEntry>) => Promise<void>;
  onDelete: (id: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  weekStart, entries, projects, editingEntryId, setEditingEntryId, onSave, onDelete,
}) => {
  const days = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }),
    [weekStart],
  );

  const entriesByDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    days.forEach((d) => map.set(formatDateForApi(d), []));
    entries.forEach((e) => {
      const key = e.date;
      if (map.has(key)) map.get(key)!.push(e);
    });
    return map;
  }, [entries, days]);

  const getProject = useCallback(
    (id?: string) => (id ? projects.find((p) => p.id === id) : undefined),
    [projects],
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Grid container spacing={1} sx={{ minWidth: 900 }}>
        {days.map((day) => {
          const dateStr = formatDateForApi(day);
          const dayEntries = entriesByDay.get(dateStr) || [];
          const dayTotal = totalMinutes(dayEntries);
          const today = isToday(day);

          return (
            <Grid item xs key={dateStr} sx={{ minWidth: 120 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5, minHeight: 250, display: 'flex', flexDirection: 'column',
                  border: today ? '1px solid rgba(123, 109, 246, 0.5)' : '1px solid rgba(148, 163, 184, 0.08)',
                  bgcolor: today ? 'rgba(123, 109, 246, 0.04)' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                  <Typography variant="caption" fontWeight={600} color={today ? 'primary' : 'text.secondary'}>
                    {format(day, 'EEE')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {format(day, 'd')}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {dayEntries.map((entry) => {
                    const proj = getProject(entry.projectId);
                    const color = proj?.color || '#7b6df6';
                    return (
                      <Tooltip
                        key={entry.id}
                        title={`${proj?.name || 'No Project'}: ${entry.description} (${formatDuration(entry.durationMinutes)})`}
                      >
                        <Box
                          sx={{
                            p: 0.75, borderRadius: 1,
                            bgcolor: alpha(color, 0.12),
                            borderLeft: `2px solid ${color}`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(color, 0.2) },
                          }}
                          onClick={() =>
                            setEditingEntryId(editingEntryId === entry.id ? null : entry.id)
                          }
                        >
                          <Typography variant="caption" fontWeight={500} noWrap sx={{ color, fontSize: '0.7rem' }}>
                            {proj?.name || 'No Project'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                            {formatDuration(entry.durationMinutes)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>

                {dayTotal > 0 && (
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ mt: 1, textAlign: 'right', color: 'text.secondary' }}
                  >
                    {formatDuration(dayTotal)}
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 1 }}>
        <Typography variant="body2" fontWeight={700}>
          Weekly Total: {formatDuration(totalMinutes(entries))}
        </Typography>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Month View
// ---------------------------------------------------------------------------

interface MonthViewProps {
  currentDate: Date;
  monthEntries: TimeEntry[];
  projects: Project[];
  onDayClick: (date: Date) => void;
}

const MonthView: React.FC<MonthViewProps> = ({ currentDate, monthEntries, projects, onDayClick }) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  // Build a 6x7 grid aligned to Monday start
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const allDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const entriesByDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    monthEntries.forEach((e) => {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date)!.push(e);
    });
    return map;
  }, [monthEntries]);

  const getProjectColor = useCallback(
    (id?: string) => (id ? projects.find((p) => p.id === id)?.color || '#7b6df6' : '#7b6df6'),
    [projects],
  );

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Box>
      {/* Day-name headers */}
      <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
        {dayNames.map((dn) => (
          <Grid item xs key={dn}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
              {dn}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar grid */}
      <Grid container spacing={0.5}>
        {allDays.map((day) => {
          const dateStr = formatDateForApi(day);
          const dayEntries = entriesByDay.get(dateStr) || [];
          const dayTotal = totalMinutes(dayEntries);
          const inMonth = day.getMonth() === currentDate.getMonth();
          const today = isToday(day);

          // Unique project colors for dots
          const uniqueColors = Array.from(
            new Set(dayEntries.map((e) => getProjectColor(e.projectId))),
          ).slice(0, 4);

          return (
            <Grid item xs key={dateStr}>
              <Paper
                variant="outlined"
                onClick={() => onDayClick(day)}
                sx={{
                  p: 1, minHeight: 72, cursor: 'pointer',
                  opacity: inMonth ? 1 : 0.35,
                  border: today ? '1px solid rgba(123, 109, 246, 0.6)' : '1px solid rgba(148, 163, 184, 0.06)',
                  bgcolor: today ? 'rgba(123, 109, 246, 0.06)' : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.06)', borderColor: 'rgba(148, 163, 184, 0.2)' },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={today ? 700 : 500}
                  color={today ? 'primary' : 'text.secondary'}
                >
                  {format(day, 'd')}
                </Typography>

                {dayTotal > 0 && (
                  <Typography variant="caption" display="block" fontWeight={600} sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                    {formatDuration(dayTotal)}
                  </Typography>
                )}

                {uniqueColors.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {uniqueColors.map((c) => (
                      <Box key={c} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c }} />
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const TimeEntryPage: React.FC = () => {
  const {
    todayEntries, weekEntries, isLoading,
    fetchTodayEntries, fetchWeekEntries,
    createEntry, updateEntry, deleteEntry,
  } = useTimeTracking();
  const { projects } = useProjects();
  const { showSuccess, showError } = useNotification();

  const [view, setView] = useState<'day' | 'week' | 'month'>('day');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [deleteDialogId, setDeleteDialogId] = useState<string | null>(null);
  const [monthEntries, setMonthEntries] = useState<TimeEntry[]>([]);
  const [monthLoading, setMonthLoading] = useState(false);

  // ---- Data fetching based on view + currentDate ----

  useEffect(() => {
    if (view === 'day') {
      // The context always has todayEntries but for non-today dates we need an API call
      if (isToday(currentDate)) {
        fetchTodayEntries();
      }
    }
  }, [view, currentDate, fetchTodayEntries]);

  useEffect(() => {
    if (view === 'week') {
      const monday = getMondayOfWeek(currentDate);
      fetchWeekEntries(formatDateForApi(monday));
    }
  }, [view, currentDate, fetchWeekEntries]);

  useEffect(() => {
    if (view === 'month') {
      setMonthLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      apiService
        .getMonthEntries(year, month)
        .then((res) => {
          const data = res.data || res;
          setMonthEntries(Array.isArray(data) ? data : (data?.entries || []));
        })
        .catch(() => setMonthEntries([]))
        .finally(() => setMonthLoading(false));
    }
  }, [view, currentDate]);

  // For "day" view on non-today dates, fetch separately
  const [dayViewEntries, setDayViewEntries] = useState<TimeEntry[]>([]);
  const [dayLoading, setDayLoading] = useState(false);

  useEffect(() => {
    if (view === 'day' && !isToday(currentDate)) {
      setDayLoading(true);
      apiService
        .getDayEntries(formatDateForApi(currentDate))
        .then((res) => {
          const data = res.data || res;
          setDayViewEntries(Array.isArray(data) ? data : (data?.entries || []));
        })
        .catch(() => setDayViewEntries([]))
        .finally(() => setDayLoading(false));
    }
  }, [view, currentDate]);

  const activeDayEntries = useMemo(() => {
    if (view !== 'day') return [];
    return isToday(currentDate) ? todayEntries : dayViewEntries;
  }, [view, currentDate, todayEntries, dayViewEntries]);

  // ---- Navigation ----

  const navigateDate = (direction: 'prev' | 'next') => {
    setEditingEntryId(null);
    setCurrentDate((prev) => {
      if (view === 'day') return direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1);
      if (view === 'week') return direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1);
      return direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1);
    });
  };

  const goToday = () => {
    setEditingEntryId(null);
    setCurrentDate(new Date());
  };

  const dateLabel = useMemo(() => {
    if (view === 'day') return format(currentDate, 'EEE, MMM d, yyyy');
    if (view === 'week') {
      const monday = getMondayOfWeek(currentDate);
      const sunday = addDays(monday, 6);
      return `${format(monday, 'MMM d')} - ${format(sunday, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  }, [view, currentDate]);

  // ---- Entry total for the current view ----
  const viewTotal = useMemo(() => {
    if (view === 'day') return totalMinutes(activeDayEntries);
    if (view === 'week') return totalMinutes(weekEntries);
    return totalMinutes(monthEntries);
  }, [view, activeDayEntries, weekEntries, monthEntries]);

  // ---- Handlers ----

  const handleCreateEntry = async (data: CreateTimeEntryRequest) => {
    await createEntry(data);
    showSuccess('Time entry added');
    // Refresh the current view if needed
    if (view === 'week') fetchWeekEntries(formatDateForApi(getMondayOfWeek(currentDate)));
    if (view === 'month') {
      const res = await apiService.getMonthEntries(currentDate.getFullYear(), currentDate.getMonth() + 1);
      const data = res.data || res;
      setMonthEntries(Array.isArray(data) ? data : (data?.entries || []));
    }
  };

  const handleSaveEntry = async (id: string, data: Partial<TimeEntry>) => {
    await updateEntry(id, data);
    showSuccess('Entry updated');
    if (view === 'week') fetchWeekEntries(formatDateForApi(getMondayOfWeek(currentDate)));
    if (view === 'day' && !isToday(currentDate)) {
      const res = await apiService.getDayEntries(formatDateForApi(currentDate));
      const d = res.data || res;
      setDayViewEntries(Array.isArray(d) ? d : (d?.entries || []));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialogId) return;
    try {
      await deleteEntry(deleteDialogId);
      showSuccess('Entry deleted');
      setDeleteDialogId(null);
      setEditingEntryId(null);
      if (view === 'week') fetchWeekEntries(formatDateForApi(getMondayOfWeek(currentDate)));
      if (view === 'day' && !isToday(currentDate)) {
        const res = await apiService.getDayEntries(formatDateForApi(currentDate));
        const d = res.data || res;
        setDayViewEntries(Array.isArray(d) ? d : (d?.entries || []));
      }
      if (view === 'month') {
        const res = await apiService.getMonthEntries(currentDate.getFullYear(), currentDate.getMonth() + 1);
        const d = res.data || res;
        setMonthEntries(Array.isArray(d) ? d : (d?.entries || []));
      }
    } catch (err: any) {
      showError(err.message || 'Failed to delete entry');
    }
  };

  const handleMonthDayClick = (date: Date) => {
    setCurrentDate(date);
    setView('day');
  };

  // ---- Loading state ----
  const viewLoading =
    (view === 'day' && (isToday(currentDate) ? isLoading : dayLoading)) ||
    (view === 'week' && isLoading) ||
    (view === 'month' && monthLoading);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Time Tracking</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Log your hours, track progress, and stay on top of your day.
        </Typography>
      </Box>

      {/* Quick Entry Bar */}
      <Box sx={{ mb: 3 }}>
        <QuickEntryBar projects={projects} onSubmit={handleCreateEntry} />
      </Box>

      {/* View Toggle + Navigation */}
      <Paper
        sx={{
          p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2,
          flexWrap: 'wrap', justifyContent: 'space-between',
          border: '1px solid rgba(148, 163, 184, 0.1)',
        }}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => { if (v) setView(v); }}
          size="small"
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigateDate('prev')}>
            <ChevronLeft />
          </IconButton>

          <Typography variant="body1" fontWeight={600} sx={{ minWidth: 180, textAlign: 'center' }}>
            {dateLabel}
          </Typography>

          <IconButton size="small" onClick={() => navigateDate('next')}>
            <ChevronRight />
          </IconButton>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Today />}
            onClick={goToday}
            sx={{ ml: 1 }}
          >
            Today
          </Button>
        </Box>

        <Chip
          icon={<Schedule sx={{ fontSize: 16 }} />}
          label={formatDuration(viewTotal)}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      </Paper>

      {/* Content */}
      <Paper sx={{ p: 2, border: '1px solid rgba(148, 163, 184, 0.1)' }}>
        {viewLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Box>
        ) : (
          <>
            {view === 'day' && (
              <DayView
                entries={activeDayEntries}
                projects={projects}
                editingEntryId={editingEntryId}
                setEditingEntryId={setEditingEntryId}
                onSave={handleSaveEntry}
                onDelete={setDeleteDialogId}
              />
            )}

            {view === 'week' && (
              <WeekView
                weekStart={getMondayOfWeek(currentDate)}
                entries={weekEntries}
                projects={projects}
                editingEntryId={editingEntryId}
                setEditingEntryId={setEditingEntryId}
                onSave={handleSaveEntry}
                onDelete={setDeleteDialogId}
              />
            )}

            {view === 'month' && (
              <MonthView
                currentDate={currentDate}
                monthEntries={monthEntries}
                projects={projects}
                onDayClick={handleMonthDayClick}
              />
            )}
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialogId} onClose={() => setDeleteDialogId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Time Entry</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this time entry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogId(null)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimeEntryPage;
